import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CalendarClock, Gift, LogOut, AlertTriangle, BadgeIndianRupee, RefreshCw } from 'lucide-react';
import {
  DURATION_OPTIONS, monthlyBaseForOrg, priceForDuration, isPlanExpired,
  daysUntilExpiry,
} from '../data/store';
import { fetchWithAuth } from '../utils/api';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

// Full-page "your plan expired, renew to continue" form for org-admins.
// Replaces the entire dashboard until renewal succeeds.
export const ResubscribePage = ({ org, onLogout, onRenewed, showNotification }) => {
  const expired = isPlanExpired(org);
  const daysLeft = daysUntilExpiry(org);
  const [pricing, setPricing] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState('month');
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchWithAuth('/pricing');
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          const parse = (v) => {
            if (!v) return {};
            if (typeof v === 'string') {
              try { return JSON.parse(v); } catch { return {}; }
            }
            return v;
          };
          setPricing({ manualPrices: parse(data.manualPrices), tierPrices: parse(data.tierPrices) });
        } else {
          setLoadError('Could not load pricing — contact your Super Admin.');
        }
      } catch (e) {
        if (!cancelled) setLoadError('Network error loading pricing.');
        console.error(e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const monthlyBase = useMemo(
    () => monthlyBaseForOrg({ pricing, orgType: org?.type, modules: org?.modules }),
    [pricing, org],
  );

  const onRenew = async () => {
    if (!selectedDuration || submitting) return;
    const amount = priceForDuration(monthlyBase, selectedDuration);
    setSubmitting(true);
    try {
      const res = await fetchWithAuth('/org/resubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planDuration: selectedDuration, planAmount: amount }),
      });
      if (res.ok) {
        const updated = await res.json();
        if (showNotification) showNotification('Plan renewed — welcome back!', 'success');
        if (onRenewed) onRenewed(updated);
      } else {
        const err = await res.json().catch(() => ({}));
        if (showNotification) showNotification(err.error || 'Failed to renew plan', 'error');
      }
    } catch (e) {
      console.error(e);
      if (showNotification) showNotification('Network error during renewal', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full h-screen flex items-center justify-center p-4" style={{ background: 'var(--theme-secondary)' }}>
      <article className="w-full max-w-3xl bg-white border border-[var(--theme-tertiary)] rounded-3xl shadow-xl overflow-hidden flex flex-col">
        <header className="px-6 py-5 border-b border-[var(--theme-tertiary)] flex items-center justify-between gap-3 flex-wrap" style={{ background: 'var(--theme-secondary)' }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl text-white flex items-center justify-center shadow-md shrink-0" style={{ background: org?.theme?.primary || 'var(--theme-primary)' }} aria-hidden="true">
              <BadgeIndianRupee size={20} />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-extrabold tracking-tight text-[#0d0d0d] truncate">
                {expired ? 'Plan expired' : 'Renew plan'}
              </h1>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 truncate">
                {org?.name || 'Your organization'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[var(--theme-tertiary)] text-slate-500 hover:text-rose-500 hover:border-rose-200 text-xs font-semibold uppercase tracking-[0.14em] transition-colors"
          >
            <LogOut size={12} aria-hidden="true" /> Logout
          </button>
        </header>

        {expired && (
          <div className="px-6 py-3 bg-rose-50 border-b border-rose-100 flex items-center gap-2 text-[12px] font-medium text-rose-600">
            <AlertTriangle size={14} aria-hidden="true" />
            <span>
              Your subscription expired on <strong>{formatDate(org?.planExpiresAt)}</strong>. Pick a new plan to restore access.
            </span>
          </div>
        )}
        {!expired && daysLeft !== null && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2 text-[12px] font-medium text-amber-700">
            <CalendarClock size={14} aria-hidden="true" />
            <span>
              Current plan ends in <strong>{daysLeft} day{daysLeft === 1 ? '' : 's'}</strong> ({formatDate(org?.planExpiresAt)}). Renew to extend.
            </span>
          </div>
        )}

        <section className="px-6 py-5 flex-1 flex flex-col gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Monthly base</p>
            <p className="text-[22px] font-extrabold text-[#0d0d0d]">
              ₹{monthlyBase.toFixed(2)} <span className="text-[11px] font-semibold text-slate-400 align-middle">/mo</span>
            </p>
            <p className="text-[11px] font-medium text-slate-500">
              Computed from your current modules ({org?.type ? `${org.type} org` : 'org'}). Contact your Super Admin to change modules.
            </p>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2 flex items-center gap-1.5">
              <CalendarClock size={11} aria-hidden="true" /> Pick a duration
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {DURATION_OPTIONS.map((opt) => {
                const total = priceForDuration(monthlyBase, opt.id);
                const active = selectedDuration === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setSelectedDuration(opt.id)}
                    aria-pressed={active}
                    className={`text-left rounded-xl px-3 py-3 border transition-all ${active
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-soft)] shadow-sm'
                        : 'border-[var(--theme-tertiary)] hover:border-[var(--theme-primary-tint)] bg-white'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-[12px] font-extrabold ${active ? 'text-[var(--theme-primary)]' : 'text-[#0d0d0d]'}`}>{opt.label}</span>
                      {opt.free && <Gift size={12} className={active ? 'text-[var(--theme-primary)]' : 'text-emerald-500'} aria-hidden="true" />}
                    </div>
                    <p className="text-[11px] font-medium text-slate-500 mt-0.5">{opt.tagline}</p>
                    <p className="mt-1 text-[13px] font-extrabold text-[#0d0d0d]">
                      {opt.free ? <span className="text-emerald-600">Free</span> : <>₹{total.toFixed(2)}</>}
                    </p>
                  </button>
                );
              })}
            </div>
            {loadError && (
              <p className="mt-2 text-[11px] font-medium text-rose-500">{loadError}</p>
            )}
          </div>
        </section>

        <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3" style={{ background: 'var(--theme-secondary)' }}>
          <button
            type="button"
            onClick={onRenew}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-xs font-semibold uppercase tracking-[0.14em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: org?.theme?.primary || 'var(--theme-primary)' }}
          >
            {submitting
              ? <><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Renewing…</>
              : <>Renew · {DURATION_OPTIONS.find((d) => d.id === selectedDuration)?.label}</>}
          </button>
        </footer>
      </article>
    </div>
  );
};

ResubscribePage.propTypes = {
  org: PropTypes.object,
  onLogout: PropTypes.func.isRequired,
  onRenewed: PropTypes.func.isRequired,
  showNotification: PropTypes.func,
};

// Read-only notice for non-admins when the plan has expired. They can't do
// anything except logout (and wait for their org-admin to renew).
export const PlanExpiredNotice = ({ org, onLogout }) => (
  <div className="w-full h-screen flex items-center justify-center p-4" style={{ background: 'var(--theme-secondary)' }}>
    <article className="w-full max-w-md bg-white border border-rose-100 rounded-3xl shadow-xl overflow-hidden">
      <header className="px-6 py-6 text-center">
        <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }} aria-hidden="true">
          <AlertTriangle size={22} />
        </div>
        <h1 className="text-base font-extrabold text-[#0d0d0d]">Subscription expired</h1>
        <p className="mt-2 text-[12px] font-medium text-slate-500 leading-relaxed">
          <strong>{org?.name || 'This organization'}</strong>’s plan ended on <strong>{formatDate(org?.planExpiresAt)}</strong>.
          Please ask your organization admin to renew before signing in again.
        </p>
      </header>
      <footer className="px-6 py-4 border-t border-rose-100 flex items-center justify-center">
        <button
          type="button"
          onClick={onLogout}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold uppercase tracking-[0.14em] transition-colors"
        >
          <LogOut size={12} aria-hidden="true" /> Logout
        </button>
      </footer>
    </article>
  </div>
);

PlanExpiredNotice.propTypes = {
  org: PropTypes.object,
  onLogout: PropTypes.func.isRequired,
};
